import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function MenuCard({ item, compact = false }) {
  const { addItem, setIsOpen } = useCart();
  const hasVariants = item.variants && item.variants.length > 0;
  const optionGroups = item.option_groups || [];
  // For items with option_groups (BBQ plates) keep a single-unit customization flow.
  // For items with plain variants (lumpia, spam musubi) allow mix-and-match qty per variant.
  // Chicken Kelaguen keeps the single-variant radio (customer picks chips OR tacos, one at a time).
  const useMultiVariant = hasVariants && optionGroups.length === 0 && item.id !== "chicken_kelaguen";
  const packSize = Number(item.pack_size) || 0;
  const isDeal = useMultiVariant && packSize > 0;

  const [singleVariant, setSingleVariant] = useState(hasVariants ? item.variants[0] : null);
  const [qtyByVariant, setQtyByVariant] = useState(() =>
    useMultiVariant ? Object.fromEntries(item.variants.map(v => [v, 0])) : {}
  );
  const [groupSelections, setGroupSelections] = useState(() => {
    const init = {};
    optionGroups.forEach(g => {
      init[g.label] = (g.min || 1) === 1 && (g.max || 1) === 1 && g.options.length ? [g.options[0]] : [];
    });
    return init;
  });

  const soldOut = !!item.sold_out;
  // In multi-variant mode, image reflects the last variant the user increased (or first with qty)
  const previewVariant = useMultiVariant
    ? (item.variants.find(v => qtyByVariant[v] > 0) || item.variants[0])
    : singleVariant;
  const activeImage = (item.variant_images && previewVariant && item.variant_images[previewVariant]) || item.image;
  const totalMulti = useMultiVariant ? Object.values(qtyByVariant).reduce((a, b) => a + b, 0) : 0;

  const bumpVariant = (v, delta) => {
    setQtyByVariant(prev => {
      const next = { ...prev, [v]: Math.max(0, (prev[v] || 0) + delta) };
      if (isDeal) {
        const sum = Object.values(next).reduce((a, b) => a + b, 0);
        if (sum > packSize) return prev; // block over-cap for deals
      }
      return next;
    });
  };

  const toggleGroupOption = (group, opt) => {
    setGroupSelections(prev => {
      const cur = prev[group.label] || [];
      const isSelected = cur.includes(opt);
      const max = group.max || 1;
      let next;
      if (max === 1) next = [opt];
      else if (isSelected) next = cur.filter(x => x !== opt);
      else next = cur.length >= max ? [...cur.slice(1), opt] : [...cur, opt];
      return { ...prev, [group.label]: next };
    });
  };

  const priceLabel = `$${item.price % 1 === 0 ? item.price.toFixed(0) : item.price.toFixed(2)}`;

  const handleAdd = () => {
    if (soldOut) return toast.error(`${item.name} is sold out today`);
    if (useMultiVariant) {
      if (isDeal) {
        if (totalMulti !== packSize) {
          return toast.error(`Pick exactly ${packSize} for this deal (${totalMulti}/${packSize})`);
        }
        const parts = item.variants.filter(v => qtyByVariant[v] > 0);
        const label = parts.map(v => qtyByVariant[v] > 1 ? `${v} ×${qtyByVariant[v]}` : v).join(" + ");
        addItem(item, label, 1);
        toast.success(`${item.name} added: ${label}`, {
          action: { label: "View Cart", onClick: () => setIsOpen(true) },
        });
        setQtyByVariant(Object.fromEntries(item.variants.map(v => [v, 0])));
        return;
      }
      if (totalMulti < 1) return toast.error("Pick at least one");
      const parts = item.variants.filter(v => qtyByVariant[v] > 0);
      parts.forEach(v => addItem(item, v, qtyByVariant[v]));
      const summary = parts.map(v => qtyByVariant[v] > 1 ? `${v} ×${qtyByVariant[v]}` : v).join(", ");
      toast.success(`${item.name} added: ${summary}`, {
        action: { label: "View Cart", onClick: () => setIsOpen(true) },
      });
      setQtyByVariant(Object.fromEntries(item.variants.map(v => [v, 0])));
      return;
    }
    if (hasVariants && !singleVariant) return toast.error("Please pick an option");
    for (const g of optionGroups) {
      const sel = groupSelections[g.label] || [];
      if (sel.length < (g.min || 0)) {
        return toast.error(`Please pick ${g.min > 1 ? `at least ${g.min}` : "a"} ${g.label.toLowerCase()}`);
      }
    }
    const label = optionGroups.length
      ? optionGroups.map(g => (groupSelections[g.label] || []).join(", ")).filter(Boolean).join(" · ")
      : singleVariant;
    addItem(item, label, 1);
    toast.success(`${item.name}${label ? ` – ${label}` : ""} added`, {
      action: { label: "View Cart", onClick: () => setIsOpen(true) },
    });
  };

  const renderMultiVariant = (dense = false) => (
    <div className={`${dense ? "space-y-1" : "space-y-1.5"}`}>
      <div className={`${dense ? "text-[10px]" : "text-[11px]"} font-semibold text-[#7A6C5D]`}>
        {isDeal ? `Pick ${packSize} total (${totalMulti}/${packSize})` : "Pick how many of each"}
      </div>
      {item.variants.map(v => {
        const qty = qtyByVariant[v] || 0;
        const plusDisabled = isDeal && totalMulti >= packSize;
        return (
          <div
            key={v}
            data-testid={`variant-row-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
            className={`flex items-center justify-between gap-2 rounded-xl border ${qty > 0 ? "border-[#D9381E] bg-[#FDECE8]" : "border-[#E5D9C5] bg-white"} px-2 py-1`}
          >
            <span className={`${dense ? "text-[11px]" : "text-xs"} font-semibold text-[#2C241B] truncate`}>{v}</span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                data-testid={`variant-minus-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                onClick={() => bumpVariant(v, -1)}
                disabled={qty === 0}
                className="w-6 h-6 rounded-full flex items-center justify-center border border-[#E5D9C5] text-[#2C241B] disabled:opacity-40 hover:bg-[#F5EEE0]"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span data-testid={`variant-qty-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`} className="w-5 text-center text-xs font-bold">{qty}</span>
              <button
                type="button"
                data-testid={`variant-plus-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                onClick={() => bumpVariant(v, 1)}
                disabled={plusDisabled}
                className="w-6 h-6 rounded-full flex items-center justify-center bg-[#D9381E] text-white hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderSingleVariantRow = () => (
    <div className="flex flex-wrap gap-1">
      {item.variants.map(v => (
        <button
          key={v}
          data-testid={`variant-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
          onClick={() => setSingleVariant(v)}
          className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${singleVariant === v ? "border-[#D9381E] bg-[#FDECE8] text-[#D9381E]" : "border-[#E5D9C5] bg-white text-[#2C241B] hover:bg-[#F5EEE0]"}`}
        >
          {v}
        </button>
      ))}
    </div>
  );

  const renderOptionGroups = (dense = false) => (
    <div className={dense ? "space-y-1.5" : "space-y-2.5"}>
      {optionGroups.map(g => {
        const sel = groupSelections[g.label] || [];
        const multi = (g.max || 1) > 1;
        return (
          <div key={g.label}>
            <div className={`${dense ? "text-[10px]" : "text-[11px]"} font-semibold text-[#7A6C5D] mb-1`}>
              {g.label} <span className="text-[#9C8F80] font-normal">· {multi ? `pick ${g.min}-${g.max}` : "pick 1"}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {g.options.map(opt => {
                const active = sel.includes(opt);
                return (
                  <button
                    key={opt}
                    data-testid={`opt-${item.id}-${g.label.toLowerCase()}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                    type="button"
                    onClick={() => toggleGroupOption(g, opt)}
                    className={`${dense ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1"} font-semibold rounded-full border-2 transition-colors ${active ? "border-[#D9381E] bg-[#FDECE8] text-[#D9381E]" : "border-[#E5D9C5] bg-white text-[#2C241B] hover:bg-[#F5EEE0]"}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (compact) {
    return (
      <div
        data-testid={`menu-card-${item.id}`}
        className={`card-lift bg-white rounded-2xl border border-[#E5D9C5] shadow-[0_4px_14px_rgba(44,36,27,0.05)] flex items-stretch overflow-hidden ${soldOut ? "opacity-70" : ""}`}
      >
        {item.image ? (
          <div className="relative w-24 sm:w-28 shrink-0 bg-[#F5EEE0] overflow-hidden">
            <img
              src={activeImage}
              alt={item.name}
              className={`w-full h-full object-cover ${item.id === 'chicken_kelaguen' && previewVariant === 'With chips' ? 'kelaguen-img-rotate' : ''}`}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.opacity = "0"; }}
            />
            {soldOut && (
              <div className="absolute inset-0 bg-black/55 flex items-center justify-center" data-testid={`sold-out-overlay-${item.id}`}>
                <span className="text-white text-[10px] font-extrabold tracking-widest rotate-[-8deg]">SOLD OUT</span>
              </div>
            )}
          </div>
        ) : (
          <div className="w-3 shrink-0 bg-[#2C5E3B]" aria-hidden />
        )}
        <div className="flex-1 min-w-0 p-3 flex flex-col gap-1.5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display font-extrabold text-base text-[#2C241B] leading-tight truncate" data-testid={`menu-name-${item.id}`} title={item.name}>
              {item.name}
            </h3>
            <span data-testid={`price-badge-${item.id}`} className="font-display font-extrabold text-base text-[#D9381E] shrink-0">{priceLabel}</span>
          </div>
          {item.description && (
            <p className="text-[11px] sm:text-xs text-[#7A6C5D] leading-snug line-clamp-2">{item.description}</p>
          )}
          {useMultiVariant && renderMultiVariant(true)}
          {!useMultiVariant && hasVariants && renderSingleVariantRow()}
          {optionGroups.length > 0 && renderOptionGroups(true)}
          <button
            data-testid={`add-to-cart-${item.id}`}
            onClick={handleAdd}
            disabled={soldOut || (useMultiVariant && (isDeal ? totalMulti !== packSize : totalMulti < 1))}
            className="cta-primary self-start mt-auto rounded-full px-3 py-1.5 font-semibold text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" /> {soldOut ? "Sold Out" : isDeal ? (totalMulti === packSize ? "Add deal" : `Add deal · ${totalMulti}/${packSize}`) : useMultiVariant && totalMulti > 0 ? `Add ${totalMulti}` : "Add"}
          </button>
        </div>
      </div>
    );
  }

  // Full-size card (Plates & Kelaguen)
  return (
    <div
      data-testid={`menu-card-${item.id}`}
      className={`card-lift bg-white rounded-3xl overflow-hidden border border-[#E5D9C5] shadow-[0_8px_30px_rgba(44,36,27,0.06)] flex flex-col ${soldOut ? "opacity-70" : ""}`}
    >
      {item.image && (
        <div className={`relative overflow-hidden bg-[#F5EEE0] ${item.id === 'pork_plate' ? 'h-64 sm:h-72 lg:h-80' : 'h-56 sm:h-64 lg:h-72'}`}>
          <img
            src={activeImage}
            alt={item.name}
            className={`w-full h-full object-cover ${item.id === 'pork_plate' ? 'plate-img-rotate' : ''}${item.id === 'chicken_kelaguen' && previewVariant === 'With chips' ? 'kelaguen-img-rotate' : ''}`}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = "0"; }}
          />
          <div className="absolute top-3 left-3 chip" data-testid={`price-badge-${item.id}`}>{priceLabel}</div>
          {soldOut && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-testid={`sold-out-overlay-${item.id}`}>
              <span className="rotate-[-6deg] bg-[#D9381E] text-white font-display font-extrabold text-2xl px-6 py-2 rounded-2xl shadow-lg tracking-wider">SOLD OUT</span>
            </div>
          )}
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display font-extrabold text-xl text-[#2C241B]" data-testid={`menu-name-${item.id}`}>{item.name}</h3>
        <p className="mt-1.5 text-xs sm:text-sm text-[#7A6C5D] leading-relaxed">{item.description}</p>
        {item.includes && item.includes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {item.includes.map((inc) => (
              <span key={inc} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F5EEE0] text-[#2C5E3B] border border-[#E5D9C5] font-medium">{inc}</span>
            ))}
          </div>
        )}
        {useMultiVariant && (
          <div className="mt-3">{renderMultiVariant(false)}</div>
        )}
        {!useMultiVariant && hasVariants && (
          <div className="mt-3">
            <div className="text-[11px] font-semibold text-[#7A6C5D] mb-1.5">Choose one</div>
            <div className="flex flex-wrap gap-1.5">
              {item.variants.map(v => (
                <button
                  key={v}
                  data-testid={`variant-${item.id}-${v.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                  onClick={() => setSingleVariant(v)}
                  className={`text-xs font-semibold rounded-full px-3 py-1 border-2 transition-colors ${singleVariant === v ? "border-[#D9381E] bg-[#FDECE8] text-[#D9381E]" : "border-[#E5D9C5] bg-white text-[#2C241B] hover:bg-[#F5EEE0]"}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}
        {optionGroups.length > 0 && <div className="mt-3">{renderOptionGroups(false)}</div>}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="font-display font-extrabold text-xl text-[#D9381E]">{priceLabel}</div>
          <button
            data-testid={`add-to-cart-${item.id}`}
            onClick={handleAdd}
            disabled={soldOut || (useMultiVariant && (isDeal ? totalMulti !== packSize : totalMulti < 1))}
            className="cta-primary rounded-full px-4 py-2 font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> {soldOut ? "Sold Out" : isDeal ? (totalMulti === packSize ? "Add deal to Cart" : `Add deal · ${totalMulti}/${packSize}`) : useMultiVariant && totalMulti > 0 ? `Add ${totalMulti}` : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
